// Site detail — buildings within a single site (buildspec 20-ui §1/§2
// and PRD §7.2.1).
//
// Visual style: the redesigned "minimal list" layout from the design
// handoff — a single white card containing each building as a divider
// row (name + FLOC + dot+label status). No progress bars per product
// direction. Field staff navigate by tapping a building in the list;
// the page intentionally has no bottom-bar CTAs — neither a
// "Site-level capture" entry point nor a "Continue" deep-link. Adding
// assets at the site level would breach AGENTS.md §Field additions
// and PRD §6.5 (field staff are scoped to asset-type variants at the
// leaf / Space level only), so the page surfaces no add affordance
// either.
//
// Role-aware card tap (buildspec 20-ui §1.1 / §2): tapping a
// building card triggers different behaviour depending on the caller:
//
//   * `field_staff` — the handler fires `POST /capture/sessions` for
//     the building, then navigates to `/capture/buildings/:id` on
//     success. Deep-link safety is preserved: session creation is
//     gated on this explicit user gesture, never on route entry.
//   * `admin` — the handler navigates only (no POST). Admins who
//     follow a capture link from the admin tree (issue #16) land in
//     the read-only Admin Preview experience on the target page
//     (buildspec 20-ui §1.1a); creating a session under the admin's
//     own user_id would create a phantom field_staff session, which
//     is exactly what the preview mode exists to prevent.
//
// The session-start network call is a typed stub for this scope
// (issue #100, C1.8). The real adapter lands with C2.2 + C2.7 when
// the capture client is wired to `POST /capture/sessions`. Because
// the stub signature mirrors the eventual adapter (takes a building
// id, resolves with the session summary or rejects with ApiError),
// swapping it later is a one-line change in this file. Admins never
// hit the stub so no code-path behaviour changes for them on swap.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react'
import { Search as SearchIcon } from 'lucide-react'
import { useNavigate, useParams } from 'react-router'

import type { BuildingStatus } from '@citycare/shared'

import { Alert } from '../../ui/alert'
import { EmptyState } from '../../ui/empty-state'
import { Spinner } from '../../ui/spinner'
import { useAuth } from '../../app/auth-provider'
import { hasTokenPrefixOnAny } from '../../lib/token-prefix-search'
import { matchesWildcardOnAny } from '../../lib/wildcard-search'
import {
  useBuildings,
  type UseBuildingsParent,
  type UseBuildingsRow,
} from '../../hooks/powersync/use-buildings'
import {
  CaptureFilterChips,
  type CaptureStatusFilter,
} from '../components/capture-filter-chips'
import { CaptureLayout } from '../components/capture-layout'
import { StatusDotLabel } from '../components/status-dot-label'
import type { CaptureBreadcrumbProps } from '../components/capture-breadcrumb'

// SiteDetailPage building row — narrows the snake_case PowerSync row
// down to the camelCase shape this page renders. Keeping the field
// list aligned with the prior REST hook minimises churn in the
// `BuildingMinimalRow` component below.
interface BuildingViewRow {
  id: string
  name: string
  floc: string | null
  status: BuildingStatus
}

function projectBuilding(row: UseBuildingsRow): BuildingViewRow {
  return {
    id: row.id,
    // PowerSync types text columns as nullable; the Drizzle migration
    // declares them NOT NULL, so the empty-string fallback is a type
    // safety net rather than a runtime path.
    name: row.name ?? '',
    floc: row.floc,
    status: row.status,
  }
}

// Typed stub for POST /capture/sessions. Swapped for the real
// capture-client dispatch in C2.2 / C2.7 (buildspec 20-ui §6.2).
// Kept as a module-level named export so the swap site is grep-able
// from the PR review. Resolves immediately so SiteDetailPage can
// exercise its success-path navigation today; the stub's signature
// is the same shape the real adapter will return (a session id).
interface StartBuildingCaptureResult {
  readonly sessionId: string
}

async function startBuildingCaptureSessionStub(
  buildingId: string,
): Promise<StartBuildingCaptureResult> {
  return { sessionId: `pending-session-for-${buildingId}` }
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function SiteDetailPage(): ReactElement {
  const { siteId = '' } = useParams<{ siteId: string }>()
  const navigate = useNavigate()
  const auth = useAuth()
  const { buildings, parent, isLoading, error } = useBuildings(siteId)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<CaptureStatusFilter>('all')
  const debouncedQuery = useDebouncedValue(searchQuery, 150)

  const role = auth.user?.role

  // Project the snake_case PowerSync rows once, here, into the
  // camelCase shape the rest of this page consumes. Keeps the
  // BuildingMinimalRow free of column-naming concerns.
  const viewBuildings = useMemo<BuildingViewRow[]>(
    () => buildings.map(projectBuilding),
    [buildings],
  )

  const filteredBuildings = useMemo(() => {
    // Token-prefix match against name + FLOC, OR-ed with a wildcard/
    // substring match. Building codes are colon-separated
    // ("BHM:BLD:Z1:C57") and colons are not token boundaries, so the
    // fragment field staff actually know ("C57", "*C57") would
    // otherwise return nothing. The wildcard branch only widens the
    // match — every query the token-prefix rule accepted still passes.
    // The status filter is independent of the query — both must pass.
    return viewBuildings.filter((building) => {
      if (statusFilter !== 'all' && building.status !== statusFilter) {
        return false
      }
      const searchFields = [building.name, building.floc]
      return (
        hasTokenPrefixOnAny(searchFields, debouncedQuery) ||
        matchesWildcardOnAny(searchFields, debouncedQuery)
      )
    })
  }, [viewBuildings, debouncedQuery, statusFilter])

  // Map the PowerSync hook's `{ isLoading, error }` to the screen's
  // 'loading' | 'error' | 'ready' discriminator. The error wins over
  // isLoading when both are present so the alert renders, not a
  // perpetual spinner.
  const errorMessage = error?.message ?? null
  const status: 'loading' | 'error' | 'ready' =
    errorMessage !== null ? 'error' : isLoading ? 'loading' : 'ready'

  const onBuildingTap = useCallback(
    (building: BuildingViewRow) => {
      const target = `/capture/buildings/${encodeURIComponent(building.id)}`
      // Admin preview mode (buildspec 20-ui §1.1a): no POST, navigate
      // directly. Field_staff is the default — the explicit-gesture
      // session create happens before navigation (§1.1 deep-link
      // safety).
      if (role === 'admin') {
        navigate(target)
        return
      }
      void startBuildingCaptureSessionStub(building.id).then(
        () => {
          navigate(target)
        },
        () => {
          // Start-capture failures get a real UX path with C2.2 — the
          // stub never rejects. A dedicated toast surface and retry
          // affordance land alongside the real POST adapter.
        },
      )
    },
    [navigate, role],
  )

  return (
    <CaptureLayout breadcrumb={buildSiteDetailBreadcrumb(parent)}>
      <section className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        {/* Title block — eyebrow + H1 + count summary. */}
        <header className="pt-2">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-cc-charcoal)]/55">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
            Site
          </span>
          <h1 className="mt-2 font-display text-3xl font-semibold leading-tight tracking-tight text-[color:var(--color-cc-forest)]">
            {parent?.site.name ?? 'Site'}
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-cc-charcoal)]/65">
            {viewBuildings.length} {viewBuildings.length === 1 ? 'building' : 'buildings'}
          </p>
        </header>

        {status === 'loading' ? (
          <div className="flex justify-center py-8">
            <Spinner label="Loading buildings…" size="md" />
          </div>
        ) : null}

        {status === 'error' ? (
          <Alert variant="error" title="Could not load buildings">
            {errorMessage ?? 'Unexpected error'}
          </Alert>
        ) : null}

        {status === 'ready' ? (
          <>
            {viewBuildings.length === 0 ? (
              <EmptyState title="No buildings yet in this site." />
            ) : (
              <>
                {/* Sticky search + filter chip row. Wrapper sticks
                     just below the layout's existing forest header so
                     the controls stay reachable as the list scrolls. */}
                <div className="sticky top-[44px] z-[5] -mx-4 flex flex-col gap-2.5 bg-[color:var(--color-cc-cream)] px-4 pb-2 pt-2 sm:top-[56px] sm:-mx-6 sm:px-6">
                  <label className="flex h-12 items-center gap-2 rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white px-3.5 shadow-sm">
                    <SearchIcon
                      aria-hidden="true"
                      className="h-4 w-4 text-[color:var(--color-cc-charcoal)]/60"
                    />
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search"
                      aria-label="Search buildings"
                      // Suppress the browser's native search-clear glyph so the
                      // themed × button is the only clear affordance — without
                      // this WebKit/Chromium and Edge-legacy render a second
                      // grey X side-by-side with ours. Mirrors the pattern in
                      // add-asset-search-results.tsx.
                      className="min-w-0 flex-1 bg-transparent text-sm text-[color:var(--color-cc-charcoal)] outline-none placeholder:text-[color:var(--color-cc-charcoal)]/45 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-ms-clear]:hidden"
                    />
                    {searchQuery ? (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        aria-label="Clear search"
                        className="grid h-6 w-6 place-items-center rounded-full bg-[color:var(--color-cc-cream-2)] text-base leading-none text-[color:var(--color-cc-charcoal)]"
                      >
                        ×
                      </button>
                    ) : null}
                  </label>
                  <CaptureFilterChips
                    selected={statusFilter}
                    onSelect={setStatusFilter}
                  />
                </div>

                {filteredBuildings.length === 0 ? (
                  <EmptyState
                    title="No buildings match your filters"
                    description={
                      debouncedQuery
                        ? `No buildings found for "${debouncedQuery}"`
                        : undefined
                    }
                  />
                ) : (
                  <ul
                    aria-label="Building list"
                    className="overflow-hidden rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white shadow-sm"
                    data-testid="building-list"
                  >
                    {filteredBuildings.map((building) => (
                      <li key={building.id}>
                        <BuildingMinimalRow
                          building={building}
                          onSelect={onBuildingTap}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            {/* No bottom-bar CTAs by design — field staff navigate by
                 tapping a building row. Adding any "Add asset" or
                 "Site-level capture" affordance here would breach
                 AGENTS.md §Field additions / PRD §6.5 (field staff are
                 scoped to asset-type variants at the leaf level). */}
          </>
        ) : null}
      </section>
    </CaptureLayout>
  )
}

// Project the loaded `parent` from `useBuildings` onto the
// CaptureLayout breadcrumb shape. Mirrors the building-/room-breadcrumb
// helper pattern. Falls back to a generic "Site" label while the
// buildings request is in flight or before the local mirror has the
// site/base rows.
function buildSiteDetailBreadcrumb(
  parent: UseBuildingsParent | null,
): CaptureBreadcrumbProps {
  const baseName = parent?.base?.name
  const baseId = parent?.base?.id
  // Parent of a site detail page is the base capture screen; if the
  // base hasn't loaded yet, fall back to the capture root so direct/
  // deep-link loads still have a real Back destination (PR #452 Codex
  // review P2 — no more navigate(-1) fallback).
  const parentTo =
    baseId !== undefined && baseId !== ''
      ? `/capture/bases/${encodeURIComponent(baseId)}`
      : '/capture'
  return {
    parentTo,
    base:
      baseName !== undefined && baseName !== '' && baseId !== undefined
        ? {
            label: baseName,
            to: `/capture/bases/${encodeURIComponent(baseId)}`,
          }
        : undefined,
    site: { label: parent?.site?.name ?? 'Site' },
  }
}

interface BuildingMinimalRowProps {
  building: BuildingViewRow
  onSelect: (building: BuildingViewRow) => void
}

// Single divider row in the minimal-list card. The whole row is a
// `<button>` so the entire surface is the tap target (44 px min via
// the row padding + body).
function BuildingMinimalRow({
  building,
  onSelect,
}: BuildingMinimalRowProps): ReactElement {
  const hasFloc = building.floc !== null && building.floc !== ''
  return (
    <button
      type="button"
      onClick={() => onSelect(building)}
      className="flex min-h-14 w-full items-center gap-3 border-b border-[color:var(--color-cc-forest)]/10 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-[color:var(--color-cc-cream)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--color-cc-lime)]"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate font-display text-[15px] font-semibold text-[color:var(--color-cc-forest)]">
          {building.name}
        </span>
        {hasFloc ? (
          <span className="truncate font-mono text-[11px] tracking-wider text-[color:var(--color-cc-charcoal)]/60">
            {building.floc}
          </span>
        ) : null}
      </span>
      <StatusDotLabel status={building.status} />
    </button>
  )
}
